import sortBy from 'lodash/sortBy';
import between from '@inquisitive/between';

import type { InteractiveLessonSectionFragment } from './lesson-builder.graphql.generated';
import type { InteractiveLessonSectionComponentFragment } from './lesson-builder.graphql.generated';
import {
  ComponentActivity,
  ComponentSubActivity,
} from './components/component.constants';
import {
  ASSESSMENT_ACTIVITIES,
  ACTIVITIES_COMPONENT_TYPE_ACTIVITY,
  LESSON_ACTIVITIES,
  ActivitiesContentType,
} from 'global/constants';
import {
  SectionOrSubSection,
  SectionOrSubSectionOverview,
} from './activity-builder.constants';
import type { BookmarkedSectionOverviewFragment } from '../shared/bookmarks/bookmark.graphql.generated';
import type { InteractiveLessonSectionOverviewFragment } from './lesson-builder.graphql.generated';
import type { Pluck } from 'lib/gql/pluck';

export function sortComponents(
  components: InteractiveLessonSectionComponentFragment[] | undefined
): (InteractiveLessonSectionComponentFragment | ComponentSubActivity)[] {
  return sortBy(components, 'sort');
}

export function getFirstSortValue(components: { sort: string }[]) {
  return between.between(
    between.lowest,
    components[0]?.sort || between.highest
  );
}

export function getLastSortValue(components: { sort: string }[]) {
  return between.between(
    components.at(-1)?.sort || between.lowest,
    between.highest
  );
}

export function getSortByIndex({
  items,
  index,
}: {
  items: { sort: string }[];
  index: number;
}) {
  const componentBefore = items[index];
  const componentAfter = items[index + 1];
  return between.between(
    componentBefore?.sort || between.lowest,
    componentAfter?.sort || between.highest
  );
}

export function getNewSortValue({
  components,
  componentId,
}: {
  components: InteractiveLessonSectionComponentFragment[];
  componentId: string | undefined;
}) {
  const componentIndex = components.findIndex(comp => comp.id === componentId);
  const componentBefore = components[componentIndex];
  const componentAfter = components[componentIndex + 1];
  return between.between(
    componentBefore?.sort || between.lowest,
    componentAfter?.sort || between.highest
  );
}

export function generateKeyBasedOnSort({
  id,
  components,
}: {
  id: string;
  components: InteractiveLessonSectionComponentFragment[];
}) {
  return `${id}-${components.map(c => c.sort).join(',')}`;
}

export function getSectionsAndSubSections(
  sections: InteractiveLessonSectionFragment[]
): SectionOrSubSection[] {
  return sortBy(sections, 'sort').flatMap(s => {
    return [s, ...(sortBy(s.subSections, 'sort') || [])];
  });
}

export function getSectionsAndSubSectionsOverview(
  sections: (
    | InteractiveLessonSectionOverviewFragment
    | BookmarkedSectionOverviewFragment
  )[]
): SectionOrSubSectionOverview[] {
  return sections.flatMap(s => {
    return [s, ...(sortBy(s.subSectionsOverview, 'sort') || [])];
  });
}

export function getSectionActivity({
  section,
  sections,
}: {
  section: SectionOrSubSection;
  sections: InteractiveLessonSectionFragment[];
}) {
  const isSubSlide = !!(
    section as Pluck<InteractiveLessonSectionFragment, 'subSections'>
  )?.parentSectionId;

  return isSubSlide
    ? (sections
        .find(
          s =>
            s.id ===
            (section as Pluck<InteractiveLessonSectionFragment, 'subSections'>)
              .parentSectionId
        )
        ?.components.find(
          c => c.type === ACTIVITIES_COMPONENT_TYPE_ACTIVITY
        ) as ComponentActivity)
    : (section.components.find(
        c => c.type === ACTIVITIES_COMPONENT_TYPE_ACTIVITY
      ) as ComponentActivity);
}

export function getInteractiveContentType() {
  const contentType: ActivitiesContentType = window.location.pathname.includes(
    '/lesson'
  )
    ? LESSON_ACTIVITIES
    : ASSESSMENT_ACTIVITIES;

  return contentType;
}

export function getInteractiveContentLabel(
  contentType?: ActivitiesContentType
) {
  const interactiveContentType = contentType || getInteractiveContentType();
  return interactiveContentType === LESSON_ACTIVITIES ? 'Lesson' : 'Assessment';
}
